import React, { useMemo, useState } from 'react';
import {
    GraduationCap, Search, ArrowLeft, CheckCircle2, Circle, CircleDot, Clock, X,
    Compass, FolderCog, ClipboardCheck, Map as MapIcon, Users, PencilRuler, Megaphone,
    FileText, LayoutGrid, ShieldCheck, HeartPulse, Contact, FileSignature, HardHat,
    Receipt, Wallet, Gauge, Shield,
} from 'lucide-react';
import { ModuleTile } from '../project-hub/ModuleTile';
import type { AccentKey } from '../project-hub/moduleAccents';
import type { TutorialBereich, TutorialKapitel, TutorialKapitelId } from '../../tutorial/types';
import {
    BEREICH_LABEL,
    FortschrittKarte,
    fortschrittJeBereich,
    lernpfadMitFortschritt,
    sucheKapitel,
    zaehleFortschritt,
} from '../../services/tutorial/tutorialLogic';

/**
 * LERNZENTRUM — Startseite (Plan §2.2).
 *
 * Drei Wege zum selben Kapitel, weil Menschen unterschiedlich suchen:
 * der Lernpfad (fuer den, der von vorn anfaengt), die Bereichskacheln (fuer
 * den, der weiss, womit er arbeitet) und die Suche (fuer den, der ein Wort im
 * Kopf hat). Alle drei enden bei `onOeffneKapitel`.
 *
 * Reine Darstellung — laedt nichts, schreibt nichts.
 */

interface Props {
    /** Alle fuer dieses Profil sichtbaren Kapitel, Registerreihenfolge. */
    kapitel: readonly TutorialKapitel[];
    /** Dieselben Kapitel nach Voraussetzungen sortiert. */
    pfad: readonly TutorialKapitel[];
    fortschritt: FortschrittKarte;
    /** Kuratierte Stolperfallen — die Suche greift auch darauf zu. */
    stolperfallen: ReadonlyMap<TutorialKapitelId, string[]>;
    laedt: boolean;
    onOeffneKapitel: (id: TutorialKapitelId) => void;
    /** Der Uebungsplatz (Auftrag A4) oder sein Platzhalter — der Container entscheidet. */
    uebungsplatz?: React.ReactNode;
}

/** Symbol und Farbe je Bereich; gleiche Bildsprache wie das Modulraster des Projekt-Hubs. */
const OPTIK: Record<TutorialBereich, { icon: React.ReactNode; accent: AccentKey }> = {
    einstieg:   { icon: <Compass className="w-5 h-5 text-blue-600" />,          accent: 'blue' },
    projekt:    { icon: <FolderCog className="w-5 h-5 text-indigo-600" />,      accent: 'indigo' },
    begehung:   { icon: <ClipboardCheck className="w-5 h-5 text-emerald-600" />, accent: 'emerald' },
    sigeplan:   { icon: <MapIcon className="w-5 h-5 text-blue-600" />,          accent: 'blue' },
    einweisung: { icon: <Users className="w-5 h-5 text-cyan-600" />,            accent: 'cyan' },
    planung:    { icon: <PencilRuler className="w-5 h-5 text-teal-600" />,      accent: 'teal' },
    vorank:     { icon: <Megaphone className="w-5 h-5 text-amber-600" />,       accent: 'amber' },
    unterlage:  { icon: <FileText className="w-5 h-5 text-violet-600" />,       accent: 'violet' },
    asplan:     { icon: <LayoutGrid className="w-5 h-5 text-stone-600" />,      accent: 'stone' },
    bauordnung: { icon: <ShieldCheck className="w-5 h-5 text-fuchsia-600" />,   accent: 'fuchsia' },
    sifa:       { icon: <HeartPulse className="w-5 h-5 text-teal-600" />,       accent: 'teal' },
    crm:        { icon: <Contact className="w-5 h-5 text-cyan-600" />,          accent: 'cyan' },
    angebot:    { icon: <FileSignature className="w-5 h-5 text-violet-600" />,  accent: 'violet' },
    freelancer: { icon: <HardHat className="w-5 h-5 text-amber-600" />,         accent: 'amber' },
    rechnung:   { icon: <Receipt className="w-5 h-5 text-indigo-600" />,        accent: 'indigo' },
    buchhaltung:{ icon: <Wallet className="w-5 h-5 text-amber-600" />,          accent: 'amber' },
    erp:        { icon: <Gauge className="w-5 h-5 text-stone-600" />,           accent: 'stone' },
    admin:      { icon: <Shield className="w-5 h-5 text-blue-600" />,           accent: 'blue' },
};

const SectionHeading: React.FC<{ label: string; hint?: string }> = ({ label, hint }) => (
    <div className="flex items-center gap-3 mb-2.5 px-1">
        <h2 className="text-[11px] font-black uppercase tracking-[0.18em] text-slate-400 whitespace-nowrap">{label}</h2>
        <div className="h-px flex-1 bg-gradient-to-r from-slate-200 to-transparent" />
        {hint && <span className="text-[11px] text-slate-400 hidden md:inline">{hint}</span>}
    </div>
);

/** Eine Kapitelzeile — im Lernpfad, in der Bereichsliste und im Suchergebnis dieselbe. */
const KapitelZeile: React.FC<{
    kapitel: TutorialKapitel;
    nummer?: number;
    gesehen: boolean;
    abgeschlossen: boolean;
    hervorgehoben?: boolean;
    onClick: () => void;
}> = ({ kapitel, nummer, gesehen, abgeschlossen, hervorgehoben, onClick }) => (
    <button
        type="button"
        onClick={onClick}
        className={`w-full text-left flex items-center gap-3 px-3.5 py-2.5 rounded-xl border transition-colors ${hervorgehoben
            ? 'bg-blue-50 border-blue-200 hover:bg-blue-100/70'
            : 'bg-white border-slate-200 hover:bg-slate-50'}`}
    >
        <span className="shrink-0">
            {abgeschlossen
                ? <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                : gesehen
                    ? <CircleDot className="w-4 h-4 text-blue-500" />
                    : <Circle className="w-4 h-4 text-slate-300" />}
        </span>
        {nummer !== undefined && <span className="shrink-0 text-[11px] font-bold text-slate-400 tabular w-5">{nummer}</span>}
        <span className="min-w-0 flex-1">
            <span className="block text-sm font-semibold text-slate-700 truncate">{kapitel.titel}</span>
            <span className="block text-[11px] text-slate-400 truncate">{BEREICH_LABEL[kapitel.bereich] ?? kapitel.bereich}</span>
        </span>
        <span className="shrink-0 text-[11px] text-slate-400 flex items-center gap-1">
            <Clock className="w-3 h-3" />{kapitel.dauerMin} Min.
        </span>
        {hervorgehoben && <span className="shrink-0 text-[10px] font-bold uppercase tracking-wider text-blue-600">Weiter hier</span>}
    </button>
);

export const LernzentrumStart: React.FC<Props> = ({
    kapitel, pfad, fortschritt, stolperfallen, laedt, onOeffneKapitel, uebungsplatz,
}) => {
    const [suche, setSuche] = useState('');
    const [bereich, setBereich] = useState<TutorialBereich | null>(null);

    const gesamt = useMemo(() => zaehleFortschritt(kapitel, fortschritt), [kapitel, fortschritt]);
    const bereiche = useMemo(() => fortschrittJeBereich(kapitel, fortschritt), [kapitel, fortschritt]);
    const pfadEintraege = useMemo(() => lernpfadMitFortschritt(pfad, fortschritt), [pfad, fortschritt]);
    const treffer = useMemo(
        () => (suche.trim() ? sucheKapitel(kapitel, suche, stolperfallen) : []),
        [kapitel, suche, stolperfallen],
    );

    const bereichsKapitel = useMemo(
        () => (bereich ? kapitel.filter((k) => k.bereich === bereich) : []),
        [kapitel, bereich],
    );

    const zeile = (k: TutorialKapitel, nummer?: number, hervorgehoben?: boolean) => {
        const stand = fortschritt.get(k.id);
        return (
            <KapitelZeile
                key={k.id}
                kapitel={k}
                nummer={nummer}
                gesehen={!!stand?.gesehenAm || !!stand?.abgeschlossenAm}
                abgeschlossen={!!stand?.abgeschlossenAm}
                hervorgehoben={hervorgehoben}
                onClick={() => onOeffneKapitel(k.id)}
            />
        );
    };

    return (
        <div data-glass-view="lernzentrum" className="h-full overflow-y-auto bg-slate-50">
            <div className="max-w-5xl mx-auto px-4 md:px-8 py-6 pb-24">

                {/* ── Kopf mit Gesamtfortschritt ── */}
                <div className="flex items-start gap-3 mb-5">
                    <div className="p-2.5 rounded-xl bg-blue-50 border border-blue-100 shrink-0">
                        <GraduationCap className="w-5 h-5 text-blue-600" />
                    </div>
                    <div className="min-w-0 flex-1">
                        <h1 className="text-xl font-bold text-slate-800 leading-tight">Lernzentrum</h1>
                        <p className="text-xs text-slate-500 mt-0.5">
                            {gesamt.abgeschlossen} von {gesamt.gesamt} Kapiteln abgeschlossen
                            {laedt && <span className="text-slate-400"> · Fortschritt wird geladen …</span>}
                        </p>
                        <div className="mt-2 h-1.5 rounded-full bg-slate-200 overflow-hidden max-w-md">
                            <div className="h-full bg-blue-500 transition-all duration-500" style={{ width: `${gesamt.prozent}%` }} />
                        </div>
                    </div>
                </div>

                {/* ── Suche ── */}
                <div className="relative mb-6">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <input
                        type="search"
                        value={suche}
                        onChange={(e) => setSuche(e.target.value)}
                        placeholder="Suchen: Titel, Stichwort oder Stolperfalle …"
                        className="w-full bg-white border border-slate-200 rounded-xl pl-9 pr-9 py-2.5 text-sm text-slate-700 placeholder:text-slate-400 focus:outline-hidden focus:border-blue-300"
                    />
                    {suche && (
                        <button
                            type="button"
                            onClick={() => setSuche('')}
                            aria-label="Suche leeren"
                            className="absolute right-2.5 top-1/2 -translate-y-1/2 p-1 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100"
                        >
                            <X className="w-4 h-4" />
                        </button>
                    )}
                </div>

                {suche.trim() ? (
                    /* ── Suchergebnis ── */
                    <section className="mb-8">
                        <SectionHeading label="Suchergebnis" hint={`${treffer.length} Kapitel`} />
                        {treffer.length === 0 ? (
                            <p className="text-sm text-slate-500 bg-white border border-slate-200 rounded-xl px-4 py-6 text-center">
                                Nichts gefunden. Die Suche geht über Titel, Stichworte und Stolperfallen.
                            </p>
                        ) : (
                            <div className="space-y-1.5">{treffer.map((k) => zeile(k))}</div>
                        )}
                    </section>
                ) : bereich ? (
                    /* ── Ein Bereich ── */
                    <section className="mb-8">
                        <button
                            type="button"
                            onClick={() => setBereich(null)}
                            className="flex items-center gap-2 text-slate-400 hover:text-blue-600 transition-colors text-[11px] font-bold uppercase tracking-wider mb-3"
                        >
                            <ArrowLeft className="w-3.5 h-3.5" />Alle Bereiche
                        </button>
                        <SectionHeading label={BEREICH_LABEL[bereich] ?? bereich} hint={`${bereichsKapitel.length} Kapitel`} />
                        <div className="space-y-1.5">{bereichsKapitel.map((k) => zeile(k))}</div>
                    </section>
                ) : (
                    <>
                        {/* ── Mein Lernpfad ── */}
                        <section className="mb-8">
                            <SectionHeading label="Mein Lernpfad" hint="In dieser Reihenfolge baut alles aufeinander auf" />
                            {pfadEintraege.length === 0 ? (
                                <p className="text-sm text-slate-500 bg-white border border-slate-200 rounded-xl px-4 py-6 text-center">
                                    Für deine Rolle ist noch kein Kapitel hinterlegt.
                                </p>
                            ) : (
                                <div className="space-y-1.5">
                                    {pfadEintraege.map((e) => zeile(e.kapitel, e.nummer, e.istNaechstes))}
                                </div>
                            )}
                        </section>

                        {/* ── Bereiche ── */}
                        <section className="mb-8">
                            <SectionHeading label="Bereiche" />
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                                {bereiche.map((b) => {
                                    const optik = OPTIK[b.bereich] ?? OPTIK.einstieg;
                                    return (
                                        <ModuleTile
                                            key={b.bereich}
                                            accent={optik.accent}
                                            icon={optik.icon}
                                            title={b.label}
                                            subtitle={`${b.gesamt} ${b.gesamt === 1 ? 'Kapitel' : 'Kapitel'} · rund ${b.dauerMin} Min.`}
                                            cta="Kapitel ansehen"
                                            onClick={() => setBereich(b.bereich)}
                                            stats={{ used: b.abgeschlossen, contracted: b.gesamt }}
                                            hint={`${b.abgeschlossen} abgeschlossen, ${b.gesehen} angesehen`}
                                        />
                                    );
                                })}
                            </div>
                        </section>
                    </>
                )}

                {/* ── Übungsplatz ── */}
                {uebungsplatz && (
                    <section>
                        {uebungsplatz}
                    </section>
                )}
            </div>
        </div>
    );
};
